import { useMemo } from "react";

import { BulkEntityActionItem } from "components/BulkActions/types";
import { Module } from "types/generated";
import { useBulkSetModuleFavorite } from "shared/Module/useSetModuleFavorite";
import {
  canSetModuleFavorite,
  canUnsetModuleFavorite,
} from "shared/Module/useSetModuleFavorite/accessValidation";
import { useBulkDisableModule } from "shared/Module/useDisableModule";
import { useBulkEnableModule } from "shared/Module/useEnableModule";
import useCanDisableModule from "shared/Module/access/useCanDisableModule";
import useCanEnableModule from "shared/Module/access/useCanEnableModule";

import { ModuleBulkActions } from "./types";

const useModuleBulkActions = (): Array<
  BulkEntityActionItem<ModuleBulkActions, Module, undefined>
> => {
  const [setModuleFavorite] = useBulkSetModuleFavorite();
  const [disableModule] = useBulkDisableModule();
  const [enableModule] = useBulkEnableModule();
  const canDisableModule = useCanDisableModule();
  const canEnableModule = useCanEnableModule();

  return useMemo<Array<BulkEntityActionItem<ModuleBulkActions, Module, undefined>>>(
    () => [
      {
        key: ModuleBulkActions.AddToFavorites,
        title: "Add to favorites",
        resultTitle: "Add modules to favorites",
        condition: (module) => ({ result: canSetModuleFavorite(module) }),
        mutation: (module) => setModuleFavorite({ id: module.id, star: true }),
      },
      {
        key: ModuleBulkActions.RemoveFromFavorites,
        title: "Remove from favorites",
        resultTitle: "Remove modules from favorites",
        condition: (module) => ({ result: canUnsetModuleFavorite(module) }),
        mutation: (module) => setModuleFavorite({ id: module.id, star: false }),
      },
      {
        key: ModuleBulkActions.Disable,
        title: "Disable",
        resultTitle: "Disable modules results",
        condition: (module) => canDisableModule(module),
        mutation: (module) => disableModule({ id: module.id }),
      },
      {
        key: ModuleBulkActions.Enable,
        title: "Enable",
        resultTitle: "Enable modules results",
        condition: (module) => canEnableModule(module),
        mutation: (module) => enableModule({ id: module.id }),
      },
    ],
    [disableModule, enableModule, setModuleFavorite, canDisableModule, canEnableModule]
  );
};

export default useModuleBulkActions;
